import React, { useRef, useState, useEffect } from 'react';
import { soundManager } from '../../utils/soundManager';
import './WatchWinding.css';

const WatchWinding = ({ progress, onProgressUpdate }) => {
  const [rotation, setRotation] = useState(0);

  // Auto-rotate the watch
  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => {
        const newRotation = prev + 2; // Rotate 2 degrees per frame
        
        // Calculate progress based on rotation (3 full rotations = 1080 degrees = 100%)
        const newProgress = Math.min((newRotation / 1080) * 100, 100);
        onProgressUpdate(newProgress);

        // Play sound occasionally
        if (Math.floor(newRotation) % 30 === 0 && newRotation > 0) {
          soundManager.play('tick', 0.08);
        }

        return newRotation;
      });
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [onProgressUpdate]);

  return (
    <div className="watch-winding-container">
      {/* Progress ring (SVG) */}
      <svg className="progress-ring" viewBox="0 0 240 240">
        <defs>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.8" />
            <stop offset="50%" stopColor="#F4E5A0" stopOpacity="1" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        
        {/* Background circle */}
        <circle
          cx="120"
          cy="120"
          r="110"
          fill="none"
          stroke="rgba(212, 175, 55, 0.08)"
          strokeWidth="1.5"
        />
        
        {/* Progress circle */}
        <circle
          cx="120"
          cy="120"
          r="110"
          fill="none"
          stroke="url(#goldGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${2 * Math.PI * 110}`}
          strokeDashoffset={`${2 * Math.PI * 110 * (1 - progress / 100)}`}
          transform="rotate(-90 120 120)"
          style={{
            transition: 'stroke-dashoffset 0.3s ease',
            filter: 'drop-shadow(0 0 6px rgba(212, 175, 55, 0.3))'
          }}
        />
      </svg>

      {/* Watch mechanism */}
      <div
        className="watch-mechanism"
        style={{
          transform: `rotate(${rotation}deg)`
        }}
      >
        {/* Watch case outer ring */}
        <div className="watch-case">
          {/* Hour markers */}
          <div className="hour-markers">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="hour-marker"
                style={{
                  transform: `rotate(${i * 30}deg)`
                }}
              >
                <div className="marker-line"></div>
              </div>
            ))}
          </div>

          {/* Inner dial */}
          <div className="watch-dial">
            {/* Center logo */}
            <div className="center-emblem">
              <span className="emblem-letter">C</span>
            </div>

            {/* Visible gears - rotating at different speeds */}
            <div 
              className="gear gear-large"
              style={{ transform: `rotate(${rotation * 2}deg)` }}
            >
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="gear-tooth"
                  style={{ transform: `rotate(${i * 45}deg)` }}
                ></div>
              ))}
            </div>

            <div 
              className="gear gear-medium"
              style={{ transform: `rotate(${-rotation * 3}deg)` }}
            >
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="gear-tooth"
                  style={{ transform: `rotate(${i * 30}deg)` }}
                ></div>
              ))}
            </div>

            <div 
              className="gear gear-small"
              style={{ transform: `rotate(${rotation * 1.5}deg)` }}
            >
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="gear-tooth"
                  style={{ transform: `rotate(${i * 60}deg)` }}
                ></div>
              ))}
            </div>
          </div>

          {/* Watch hands */}
          <div className="watch-hands">
            <div 
              className="hand hour-hand"
              style={{ transform: `rotate(${(progress / 100) * 360}deg)` }}
            ></div>
            <div 
              className="hand minute-hand"
              style={{ transform: `rotate(${(progress / 100) * 360 * 12}deg)` }}
            ></div>
          </div>

          {/* Center pivot */}
          <div className="center-pivot"></div>
        </div>

        {/* Crown (winding knob) */}
        <div className="watch-crown">
          <div className="crown-grooves">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="groove"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchWinding;